var GuidedSelling = (function ($) {
    "use strict";

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------

    var conversationId = null;
    var history = [];
    var isSending = false;
    var requestToken = 0;
    var flowBusy = false;
    var flowTimer = null;
    var profile = {};
    var answers = {};
    var quizStepIds = [];
    var steps = null;
    var starterGroups = null;
    /** Starter actions are fetched once, the first time the panel is opened. */
    var starterGroupsRequested = false;
    var scrolledToAnchor = null;
    /** Rendered chat turns, in order — the source for restoring after a page load. */
    var transcript = [];
    /** Picked starter action — the welcome turn is static markup, so it has no transcript entry. */
    var starterSelection = null;
    var isRestoring = false;
    /** Conversation-level helpful vote — once set, the question is not asked again. */
    var feedbackVote = null;
    var feedbackNoteSent = false;

    // -------------------------------------------------------------------------
    // Constants
    // -------------------------------------------------------------------------

    var THINKING_MS = 400;
    /** Every country runs on the same domain, so the culture is appended to keep sessions apart. */
    var STORAGE_KEY_PREFIX = "giant.guided-selling.session";
    var STORAGE_VERSION = 2;
    /** Temporary: extra system-prompt instructions for prompt testing, set by hand in local storage. */
    var DEBUG_APPENDIX_STORAGE_KEY = "guidedSellingDebugSystemAppendix";
    var ENTRY_DATA_KEY = "guidedSellingEntry";
    /** Auto-reopen restored chat only at desktop widths (matches typical tablet/desktop split). */
    var DESKTOP_MIN_WIDTH = 1024;
    /** Matches the CSS fullscreen panel breakpoint (`max-width: 767px`). */
    var FULLSCREEN_MAX_WIDTH = 767;
    var IMAGE_TRANSFORM_FROM = "/t_Giant_400/";
    var IMAGE_TRANSFORM_WIDTH_TOKEN = "{width}";
    var IMAGE_TRANSFORM_TO = "/b_white%2Cc_pad%2Cw_" + IMAGE_TRANSFORM_WIDTH_TOKEN + "%2Cq_80/";
    /** Prototype ProductCard sizes — Medium is unused, so a suggestion is either one or the other. */
    var CARD_SIZE_LARGE = "large";
    var CARD_SIZE_SMALL = "small";
    var CARD_IMAGE_WIDTH = { large: 400, small: 200 };
    var MAX_RATING = 5;
    var LABEL_BEST_MATCH = "Best match";
    /** Shown when the API flags a suggestion as a near miss rather than a full fit. */
    var LABEL_CLOSE_MATCH = "Partial match";
    /** Prototype large-card spec rows — Groupset, Frame, Colors, in that order. */
    var LABEL_SPEC_GROUPSET = "Groupset";
    var LABEL_SPEC_FRAME = "Frame";
    var LABEL_SPEC_COLORS = "Colors";
    var MAX_SPEC_ROWS = 3;
    /** Reviews and awards are not on the guided-selling response yet — placeholder until they are. */
    var PLACEHOLDER_RATING = 5;
    var PLACEHOLDER_AWARD = "Cycling Weekly Editor's Choice 2024";
    /** Used when a suggested dealer has no SummaryImage yet. Opening hours are still a placeholder. */
    var PLACEHOLDER_DEALER_IMAGE =
        "https://static.giant-bicycles.com/Images/StoreSites/Banners/1920x600_1675345157.jpg";
    var PLACEHOLDER_DEALER_HOURS = [
        { day: "Monday", hours: "Closed" },
        { day: "Tuesday", hours: "09:00 - 18:00" },
        { day: "Wednesday", hours: "09:00 - 18:00" },
        { day: "Thursday", hours: "09:00 - 18:00" },
        { day: "Friday", hours: "09:00 - 18:00" },
        { day: "Saturday", hours: "09:00 - 17:00" },
        { day: "Sunday", hours: "Closed" }
    ];
    var LABEL_DEALER_IN_STOCK = "In stock";
    var LABEL_DEALER_GIANT_STORE = "Giant Store";
    var LABEL_DEALER_PRODUCT_FALLBACK = "This bike";
    var LABEL_DEALER_CLOSED_TODAY = "Closed today";
    var LABEL_DEALER_OPEN_NOW = "Open now - closes ";
    var LABEL_BOOK_TEST_RIDE = "Book a test ride";
    var LABEL_ORDER_FROM_STORE = "Order from store";
    var LABEL_SELECT_SIZE = "Select a size to order from this store";
    var LABEL_ORDER_ADDING = "Adding to cart…";
    var CLOCK_ICON_SVG =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />' +
        "</svg>";
    var MAP_PIN_ICON_SVG =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M20 10c0 6-8 11-8 11s-8-5-8-11a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />' +
        "</svg>";
    var CHEVRON_ICON_SVG =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="m6 9 6 6 6-6" />' +
        "</svg>";
    var STAR_ICON_SVG =
        '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">' +
        '<rect width="12" height="12" rx="1.2" fill="currentColor" />' +
        '<path d="M6 8.1 3.54 9.42l.48-2.76-2.04-1.98 2.76-.42L6 1.8l1.26 2.52 2.76.42-1.98 1.98.48 2.76L6 8.1Z" fill="#ffffff" />' +
        "</svg>";
    var TROPHY_ICON_SVG =
        '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
        '<path d="M6 4h12v2h2.5a1 1 0 0 1 1 1c0 2.5-1.7 4.6-4 5.2A6 6 0 0 1 13 15.9V18h3v2H8v-2h3v-2.1a6 6 0 0 1-4.5-3.7c-2.3-.6-4-2.7-4-5.2a1 1 0 0 1 1-1H6V4Zm0 4H4.6A3.5 3.5 0 0 0 6 10.2V8Zm12 0v2.2A3.5 3.5 0 0 0 19.4 8H18Z" fill="currentColor" />' +
        "</svg>";
    /** The comparison page shows at most three bikes side by side. */
    var MAX_COMPARE_PRODUCTS = 3;
    /** Suggested product ids are global-catalogue bike model ids, which the route expects as "gc@{id}". */
    var COMPARE_ID_PREFIX = "gc@";
    /** Matches content padding ($spacing-m) — keeps the anchored user turn clear of the top edge. */
    var ANCHOR_TOP_OFFSET = 24;

    var WELCOME_HTML =
        '<div class="guided-selling-message guided-selling-message--bot" data-guided-selling-welcome>' +
        "<p>Hi I&rsquo;m Cadence, your Giant riding partner. Tell me how you ride. I&rsquo;ll tell you what to ride.</p>" +
        '<div class="guided-selling-action-groups" data-guided-selling-starter-groups></div>' +
        "</div>" +
        '<div class="guided-selling-scroll-spacer" aria-hidden="true"></div>';

    // -------------------------------------------------------------------------
    // Config / URLs
    // -------------------------------------------------------------------------

    var Config = {
        apply: function (config) {
            if (!config) {
                return;
            }

            if (config.groups && config.groups.length) {
                starterGroups = config.groups;
            }

            if (config.steps) {
                steps = config.steps;
            }

            if (config.quizStepIds && config.quizStepIds.length) {
                quizStepIds = config.quizStepIds;
            }
        },

        getCulture: function () {
            var culture = (typeof window.culture === "string" && window.culture) ? window.culture : "global";
            return culture.replace(/^\/+|\/+$/g, "").toLowerCase() || "global";
        },

        getCulturePrefix: function () {
            return "/" + Config.getCulture();
        },

        getProxyUrl: function () {
            return Config.getCulturePrefix() + "/apiproxy/guided-selling/messages";
        },

        getFeedbackUrl: function (id) {
            return Config.getCulturePrefix() + "/apiproxy/guided-selling/conversations/" + encodeURIComponent(id);
        },

        getAddCartItemUrl: function () {
            return Config.getCulturePrefix() + "/shop/addcartitem"
                + (typeof window.store === "string" && window.store.length
                    ? "?store=" + encodeURIComponent(window.store)
                    : "");
        },

        getCartSummaryUrl: function (productId, partnumberCode) {
            var params = {
                expanded: false,
                r: Math.random(),
                divisionCode: window.divisionCode
            };

            if (typeof window.store === "string" && window.store.length) {
                params.store = window.store;
            }

            if (window.enableDiscoverModeCart === true) {
                params.isProductAddedMode = true;
                if (productId) {
                    params.addedProductId = productId;
                }
                if (partnumberCode) {
                    params.addedPartnumberCode = partnumberCode;
                }
            }

            return Config.getCulturePrefix() + "/shop/getcart?" + $.param(params);
        },

        getGaClientId: function () {
            try {
                if (window.ga && window.ga.getAll && window.ga.getAll()[0]) {
                    return window.ga.getAll()[0].get("clientId") || "";
                }
            } catch (e) { /* ignore */ }

            return "";
        },

        getStarterActionsUrl: function () {
            var url = $("#guided-selling-panel").attr("data-starter-actions-url")
                || (Config.getCulturePrefix() + "/apiproxy/guided-selling/starter-actions");
            var $body = $("body");
            var pageType = $body.attr("data-guided-selling-page-type") || "Global";
            var params = {
                pageType: pageType
            };

            if (pageType === "ProductDetail") {
                params.productId = $body.attr("data-product-id");
            } else if (pageType === "ProductLister") {
                params.categoryId = $body.attr("data-category-id");
            }

            return url + (url.indexOf("?") === -1 ? "?" : "&") + $.param(params);
        }
    };

    // -------------------------------------------------------------------------
    // ChatStorage — keeps the conversation alive across page loads (session storage)
    // -------------------------------------------------------------------------

    var ChatStorage = {
        /** Session key for the current culture — a country switch starts a fresh conversation. */
        getKey: function () {
            return STORAGE_KEY_PREFIX + "." + Config.getCulture();
        },

        getStorage: function () {
            try {
                var storage = window.sessionStorage;
                var probe = STORAGE_KEY_PREFIX + ".probe";
                storage.setItem(probe, "1");
                storage.removeItem(probe);
                return storage;
            } catch (e) {
                return null;
            }
        },

        save: function () {
            if (isRestoring) {
                return;
            }

            var storage = ChatStorage.getStorage();
            if (!storage) {
                return;
            }

            if (!transcript.length && !$("body").hasClass("guided-selling-open")) {
                ChatStorage.clear();
                return;
            }

            try {
                storage.setItem(ChatStorage.getKey(), JSON.stringify({
                    version: STORAGE_VERSION,
                    isOpen: $("body").hasClass("guided-selling-open"),
                    conversationId: conversationId,
                    history: history,
                    profile: profile,
                    answers: answers,
                    transcript: transcript,
                    starterSelection: starterSelection,
                    feedbackVote: feedbackVote,
                    feedbackNoteSent: feedbackNoteSent
                }));
            } catch (e) { /* storage full or unavailable — conversation stays in memory */ }
        },

        load: function () {
            var storage = ChatStorage.getStorage();
            if (!storage) {
                return null;
            }

            var raw = null;
            try {
                raw = storage.getItem(ChatStorage.getKey());
            } catch (e) {
                return null;
            }

            if (!raw) {
                return null;
            }

            var state = null;
            try {
                state = JSON.parse(raw);
            } catch (e) {
                ChatStorage.clear();
                return null;
            }

            if (!state || state.version !== STORAGE_VERSION) {
                ChatStorage.clear();
                return null;
            }

            return state;
        },

        clear: function () {
            var storage = ChatStorage.getStorage();
            if (!storage) {
                return;
            }

            try {
                storage.removeItem(ChatStorage.getKey());
            } catch (e) { /* ignore */ }
        },

        restore: function () {
            var state = ChatStorage.load();
            if (!state) {
                return;
            }

            isRestoring = true;

            conversationId = state.conversationId || null;
            history = $.isArray(state.history) ? state.history : [];
            profile = state.profile || {};
            answers = state.answers || {};
            transcript = $.isArray(state.transcript) ? state.transcript : [];
            starterSelection = state.starterSelection || null;
            feedbackVote = state.feedbackVote === "yes" || state.feedbackVote === "no"
                ? state.feedbackVote
                : null;
            feedbackNoteSent = !!state.feedbackNoteSent;

            var hasUserTurn = false;
            $.each(transcript, function (_, entry) {
                if (!entry || !entry.role) {
                    return;
                }

                Messaging.renderEntry(entry);
                hasUserTurn = hasUserTurn || entry.role === "user";
            });

            Panel.setResetVisible(hasUserTurn);
            Feedback.hydrate();

            // History restores on every device; the panel itself only auto-opens on desktop.
            if (state.isOpen && window.innerWidth > DESKTOP_MIN_WIDTH) {
                $("html").addClass("guided-selling-restoring");
                Panel.open({ silent: true });
                ChatStorage.restoreTransitions();
            }

            isRestoring = false;

            // Panel dimensions are only reliable once the layout settled.
            window.setTimeout(function () {
                Scroll.sync({ instant: true });
            }, 0);
        },

        restoreTransitions: function () {
            var removeClass = function () {
                $("html").removeClass("guided-selling-restoring");
            };

            if (typeof window.requestAnimationFrame !== "function") {
                window.setTimeout(removeClass, 0);
                return;
            }

            // Keep transitions off through the first paint, then restore normal animations.
            window.requestAnimationFrame(function () {
                window.requestAnimationFrame(removeClass);
            });
        }
    };

    // -------------------------------------------------------------------------
    // Panel — open / close / reset / composer chrome
    // -------------------------------------------------------------------------

    var Panel = {
        toggle: function () {
            if ($("body").hasClass("guided-selling-open")) {
                Panel.close();
            } else {
                Panel.open();
            }
        },

        open: function (options) {
            options = options || {};

            PredefinedActions.ensureLoaded();

            if ($("body").hasClass("menu-slider-open") && typeof resetMenuSlider === "function") {
                resetMenuSlider();
            }

            $("body").addClass("guided-selling-open");
            $("html").addClass("guided-selling-open");
            $(".guided-selling-toggle").attr("aria-expanded", "true");
            $("#guided-selling-panel").attr("aria-hidden", "false");

            setTimeout(function () {
                if (!options.silent) {
                    $("#guided-selling-input").trigger("focus");
                }
                Scroll.updateFades();
            }, 300);

            ChatStorage.save();
        },

        close: function () {
            $("body").removeClass("guided-selling-open");
            $("html").removeClass("guided-selling-open");
            $(".guided-selling-toggle").attr("aria-expanded", "false");
            $("#guided-selling-panel").attr("aria-hidden", "true");

            ChatStorage.save();
        },

        isFullscreen: function () {
            return window.innerWidth <= FULLSCREEN_MAX_WIDTH;
        },

        reset: function () {
            requestToken++;
            if (flowTimer) {
                window.clearTimeout(flowTimer);
                flowTimer = null;
            }

            conversationId = null;
            history = [];
            isSending = false;
            flowBusy = false;
            profile = {};
            answers = {};
            scrolledToAnchor = null;
            transcript = [];
            starterSelection = null;
            feedbackVote = null;
            feedbackNoteSent = false;
            ChatStorage.clear();

            $("#guided-selling-messages").html(WELCOME_HTML);
            PredefinedActions.render();
            $("#guided-selling-input").val("");
            DebugInfo.applyVersion(null);
            Panel.setComposerDisabled(false);
            Panel.setResetVisible(false);
            Scroll.sync();
            $("#guided-selling-input").trigger("focus");

            // Keeps the panel open on a reload, but with a blank conversation.
            ChatStorage.save();
        },

        setResetVisible: function (visible) {
            var $reset = $("#guided-selling-reset");
            if (!$reset.length) {
                return;
            }

            if (visible) {
                $reset.removeAttr("hidden");
            } else {
                $reset.attr("hidden", "hidden");
            }
        },

        setComposerDisabled: function (disabled) {
            $("#guided-selling-input").prop("disabled", disabled);
            $(".guided-selling-composer__send").prop("disabled", disabled);
        }
    };

    // -------------------------------------------------------------------------
    // PredefinedActions — starters, chips/options, quiz flow
    // -------------------------------------------------------------------------

    var PredefinedActions = {
        /** Fetches the starters on the first panel open; a failed request is retried on the next one. */
        ensureLoaded: function () {
            if (starterGroupsRequested) {
                return;
            }

            starterGroupsRequested = true;

            $.getJSON(Config.getStarterActionsUrl())
                .done(function (data) {
                    if (data && data.groups) {
                        Config.apply(data);
                        PredefinedActions.render();
                    }
                })
                .fail(function () {
                    starterGroupsRequested = false;
                });
        },

        render: function () {
            var $container = $("[data-guided-selling-starter-groups]");
            if (!$container.length) {
                return;
            }

            $container.empty();

            if (!starterGroups || !starterGroups.length) {
                return;
            }

            $container.append(PredefinedActions.buildGroups(starterGroups, starterSelection));
            Scroll.sync();
        },

        handleClick: function ($action) {
            if (!$action || !$action.length || isSending || flowBusy) {
                return;
            }

            // Quick replies share the chip styles but send a follow-up, not a starter action.
            if ($action.hasClass("guided-selling-quick-reply")) {
                return;
            }

            var label = $.trim($action.attr("data-label") || "");
            var message = $.trim($action.attr("data-message") || "");
            var starterActionId = $.trim($action.attr("data-id") || "");
            var next = $.trim($action.attr("data-next") || "");
            var complete = $action.attr("data-complete") === "true";
            var remember = PredefinedActions.parseRemember($action.attr("data-remember"));
            var isWelcomeStarter = $action.closest("[data-guided-selling-welcome]").length > 0;

            // Welcome starters always open a fresh conversation — scrolling back
            // and picking another (or the same) one must not continue the current thread.
            if (isWelcomeStarter) {
                if (transcript.length || history.length || conversationId) {
                    Panel.reset();
                }

                starterSelection = label;
                PredefinedActions.render();
            } else {
                var $groups = $action.closest(".guided-selling-action-groups");
                $groups.find(".guided-selling-action").attr("aria-pressed", "false");
                $action.attr("aria-pressed", "true");

                var entry = Messaging.getEntry($action);
                if (entry) {
                    entry.selected = label;
                }
            }

            // Local scripted flow (quiz steps): collect answers, call API only on complete.
            if (next || complete) {
                PredefinedActions.handleFlowPick({
                    label: label,
                    next: next,
                    complete: complete,
                    remember: remember
                });
                return;
            }

            if (!message && !label) {
                return;
            }

            Messaging.send(message || label, starterActionId ? { starterActionId: starterActionId } : {});
        },

        parseRemember: function (raw) {
            if (!raw) {
                return null;
            }

            try {
                return JSON.parse(raw);
            } catch (e) {
                return null;
            }
        },

        handleFlowPick: function (action) {
            if (action.remember) {
                $.extend(profile, action.remember);
                $.each(action.remember, function (key) {
                    answers[key] = action.label;
                });
            }

            Messaging.add(action.label, "user");

            if (action.complete) {
                Messaging.send(PredefinedActions.buildQuizCompletionMessage(), {
                    skipUserBubble: true
                });
                return;
            }

            if (!action.next || !steps || !steps[action.next]) {
                return;
            }

            PredefinedActions.advanceFlowStep(action.next);
        },

        buildQuizCompletionMessage: function () {
            var lines = ["I completed the bike finder quiz."];

            if (answers.terrain) {
                lines.push("Terrain: " + answers.terrain + ".");
            }
            if (answers.goal) {
                lines.push("Goal: " + answers.goal + ".");
            }
            if (answers.budget) {
                lines.push("Budget: " + answers.budget + ".");
            }

            lines.push("Please recommend the right Giant bike for me.");
            return lines.join(" ");
        },

        advanceFlowStep: function (stepId) {
            var step = steps && steps[stepId];
            if (!step) {
                return;
            }

            flowBusy = true;
            Messaging.setTyping(true);

            if (flowTimer) {
                window.clearTimeout(flowTimer);
            }

            flowTimer = window.setTimeout(function () {
                flowTimer = null;
                Messaging.setTyping(false);
                PredefinedActions.addFlowStepMessage(stepId, step);
                flowBusy = false;
            }, THINKING_MS);
        },

        addFlowStepMessage: function (stepId, step) {
            Messaging.addEntry({
                role: "bot",
                text: step.say || "",
                groups: step.groups && step.groups.length ? step.groups : null,
                progress: PredefinedActions.getQuizProgress(stepId)
            });
        },

        getQuizProgress: function (stepId) {
            var index = $.inArray(stepId, quizStepIds);
            if (index < 0) {
                return null;
            }

            return {
                step: index + 1,
                total: quizStepIds.length
            };
        },

        buildProgress: function (step, total) {
            var $progress = $("<div></div>")
                .addClass("guided-selling-progress")
                .attr("aria-label", "Question " + step + " of " + total);

            var $bars = $("<div></div>")
                .addClass("guided-selling-progress__bars")
                .attr("aria-hidden", "true");

            for (var i = 0; i < total; i++) {
                $bars.append(
                    $("<span></span>")
                        .addClass("guided-selling-progress__bar")
                        .toggleClass("is-complete", i < step)
                );
            }

            var $label = $("<p></p>")
                .addClass("guided-selling-progress__label")
                .html("<span>" + step + "</span> of " + total);

            return $progress.append($bars).append($label);
        },

        /**
         * Builds action-group UI matching the prototype ActionGroups kinds:
         * option | question | chip-primary | chip-secondary.
         */
        buildGroups: function (groups, selectedLabel) {
            var $wrap = $("<div></div>").addClass("guided-selling-action-groups__list");

            $.each(groups || [], function (_, group) {
                if (!group || !group.actions || !group.actions.length) {
                    return;
                }

                var kind = group.kind || "question";
                var isChip = kind === "chip-primary" || kind === "chip-secondary";
                var $group = $("<div></div>")
                    .addClass("guided-selling-action-group")
                    .addClass("guided-selling-action-group--" + kind);

                if (group.title) {
                    $group.append(
                        $("<p></p>")
                            .addClass("guided-selling-action-group__title")
                            .text(group.title)
                    );
                }

                var $actions = $("<div></div>").addClass(
                    isChip
                        ? "guided-selling-action-group__actions guided-selling-action-group__actions--chips"
                        : "guided-selling-action-group__actions"
                );

                $.each(group.actions, function (__, action) {
                    if (!action || !action.label) {
                        return;
                    }

                    var label = action.label;
                    var $button = $("<button></button>")
                        .attr("type", "button")
                        .attr("aria-pressed", selectedLabel === label ? "true" : "false")
                        .attr("data-label", label)
                        .addClass("guided-selling-action")
                        .addClass("guided-selling-action--" + kind);

                    if (action.id) {
                        $button.attr("data-id", action.id);
                    }
                    if (action.message) {
                        $button.attr("data-message", action.message);
                    }
                    if (action.next) {
                        $button.attr("data-next", action.next);
                    }
                    if (action.complete) {
                        $button.attr("data-complete", "true");
                    }
                    if (action.remember) {
                        $button.attr("data-remember", JSON.stringify(action.remember));
                    }

                    if (kind === "option") {
                        $button.append(
                            $("<span></span>")
                                .addClass("guided-selling-action__label")
                                .text(label)
                        );
                        if (action.description) {
                            $button.append(
                                $("<span></span>")
                                    .addClass("guided-selling-action__description")
                                    .text(action.description)
                            );
                        }
                    } else {
                        $button.text(label);
                    }

                    $actions.append($button);
                });

                $group.append($actions);
                $wrap.append($group);
            });

            return $wrap;
        },

        appendGroupsToLastBotMessage: function (groups) {
            var $lastBot = $("#guided-selling-messages .guided-selling-message--bot").last();
            if (!$lastBot.length) {
                return;
            }

            var $groups = $("<div></div>").addClass("guided-selling-action-groups");
            $groups.append(PredefinedActions.buildGroups(groups));
            $lastBot.append($groups);

            var entry = Messaging.getEntry($lastBot);
            if (entry) {
                entry.groups = groups;
                ChatStorage.save();
            }

            Scroll.sync();
        }
    };

    // -------------------------------------------------------------------------
    // QuickReplies — tap-to-send follow-ups on an assistant turn
    // -------------------------------------------------------------------------

    var QuickReplies = {
        MAX: 3,

        /** Drops blanks and duplicate labels. The API already caps at three; this matches that. */
        normalize: function (replies) {
            var normalized = [];
            var seen = {};

            $.each(replies || [], function (_, reply) {
                if (!reply || normalized.length >= QuickReplies.MAX) {
                    return;
                }

                var label = $.trim(reply.label || "");
                var message = $.trim(reply.message || "");
                var key = label.toLowerCase();

                if (!label || !message || seen[key]) {
                    return;
                }

                seen[key] = true;
                normalized.push({ label: label, message: message });
            });

            return normalized;
        },

        /**
         * Outline chips under the reply. A pick stays highlighted and every chip stays
         * clickable, same as the prototype Chip.
         */
        build: function (replies, selectedLabel) {
            var $wrap = $("<div></div>")
                .addClass("guided-selling-quick-replies")
                .attr("role", "group")
                .attr("aria-label", "Suggested replies");

            $.each(QuickReplies.normalize(replies), function (_, reply) {
                $wrap.append(
                    $("<button></button>")
                        .attr("type", "button")
                        .attr("aria-pressed", selectedLabel === reply.label ? "true" : "false")
                        .attr("data-label", reply.label)
                        .attr("data-message", reply.message)
                        .addClass("guided-selling-action")
                        .addClass("guided-selling-action--chip-secondary")
                        .addClass("guided-selling-quick-reply")
                        .text(reply.label)
                );
            });

            return $wrap;
        },

        appendToLastBotMessage: function (replies) {
            var normalized = QuickReplies.normalize(replies);
            if (!normalized.length) {
                return;
            }

            var $lastBot = $("#guided-selling-messages .guided-selling-message--bot")
                .not(".guided-selling-message--typing")
                .last();

            if (!$lastBot.length) {
                return;
            }

            $lastBot.append(QuickReplies.build(normalized));

            var entry = Messaging.getEntry($lastBot);
            if (entry) {
                entry.quickReplies = normalized;
                ChatStorage.save();
            }

            Scroll.sync();
        },

        handleClick: function ($chip) {
            if (!$chip || !$chip.length || isSending || flowBusy) {
                return;
            }

            var label = $.trim($chip.attr("data-label") || "");
            var message = $.trim($chip.attr("data-message") || "");

            if (!message && !label) {
                return;
            }

            var $replies = $chip.closest(".guided-selling-quick-replies");
            $replies.find(".guided-selling-quick-reply").attr("aria-pressed", "false");
            $chip.attr("aria-pressed", "true");

            var entry = Messaging.getEntry($chip);
            if (entry) {
                entry.selectedQuickReply = label;
            }

            Messaging.send(message || label);
        }
    };

    // -------------------------------------------------------------------------
    // DebugPrompt — temporary prompt-tweaking hook for testing
    // -------------------------------------------------------------------------

    var DebugPrompt = {
        /** Returns the hand-set local-storage appendix, or "" when unset or storage is blocked. */
        getSystemAppendix: function () {
            try {
                return $.trim(window.localStorage.getItem(DEBUG_APPENDIX_STORAGE_KEY) || "");
            } catch (e) {
                return "";
            }
        }
    };

    /**
     * Developer-only label for the agent version on the latest message response.
     * The panel sets data-showdebuginfo when Shared.IsInTestMode and Shared.IsDeveloper.
     */
    var DebugInfo = {
        isEnabled: function () {
            return $("#guided-selling-panel").attr("data-showdebuginfo") === "true";
        },

        /** Shows "(vN)" when versionNumber is set, and removes it when the response has none. */
        applyVersion: function (versionNumber) {
            if (!DebugInfo.isEnabled()) {
                return;
            }

            var $title = $("#guided-selling-panel .guided-selling-panel__title");
            if (!$title.length) {
                return;
            }

            $title.find(".guided-selling-panel__version").remove();
            $title.contents().filter(function () {
                return this.nodeType === 3 && $.trim(this.nodeValue) === "";
            }).remove();

            if (versionNumber === null || versionNumber === undefined || versionNumber === "") {
                return;
            }

            $title.append(document.createTextNode(" "));
            $title.append(
                $("<span></span>")
                    .addClass("guided-selling-panel__version")
                    .text("(v" + versionNumber + ")")
            );
        }
    };

    // -------------------------------------------------------------------------
    // Messaging — chat bubbles, typing, API
    // -------------------------------------------------------------------------

    var Messaging = {
        send: function (presetMessage, options) {
            options = options || {};
            var $input = $("#guided-selling-input");
            var message = $.trim(
                typeof presetMessage === "string" ? presetMessage : $input.val()
            );

            if (!message || isSending) {
                return;
            }

            var token = ++requestToken;
            isSending = true;

            if (!options.skipUserBubble) {
                Messaging.add(message, "user");
            }

            $input.val("");
            Messaging.setTyping(true);
            Panel.setComposerDisabled(true);

            var payload = {
                message: message,
                conversationId: conversationId,
                locale: (typeof window.cultureFull === "string" && window.cultureFull) ? window.cultureFull : null,
                divisionCode: (typeof window.divisionCode === "string" && window.divisionCode) ? window.divisionCode : null,
                history: history.slice()
            };

            if (options.starterActionId) {
                payload.starterActionId = options.starterActionId;
            }

            var debugSystemAppendix = DebugPrompt.getSystemAppendix();
            if (debugSystemAppendix) {
                payload.debugSystemAppendix = debugSystemAppendix;
            }

            history.push({ role: "user", content: message });
            ChatStorage.save();

            $.ajax({
                url: Config.getProxyUrl(),
                method: "POST",
                contentType: "application/json; charset=utf-8",
                dataType: "json",
                data: JSON.stringify(payload)
            })
                .done(function (data) {
                    if (token !== requestToken) {
                        return;
                    }

                    var reply = data && data.message
                        ? data.message
                        : "Sorry, I could not process that right now.";

                    if (data && data.conversationId) {
                        conversationId = data.conversationId;
                    }

                    DebugInfo.applyVersion(data ? data.versionNumber : null);

                    history.push({ role: "assistant", content: reply });
                    Messaging.setTyping(false);
                    Messaging.add(reply, "bot");

                    if (data && data.suggestedProducts && data.suggestedProducts.length) {
                        Products.appendSuggestionsToLastBotMessage(data.suggestedProducts);
                    }

                    if (data && data.suggestedDealers && data.suggestedDealers.length) {
                        Dealers.appendNearestToLastBotMessage(
                            data.suggestedDealers,
                            data.suggestedProducts
                        );
                    }

                    if (data && data.groups && data.groups.length) {
                        PredefinedActions.appendGroupsToLastBotMessage(data.groups);
                    }

                    if (data && data.quickReplies && data.quickReplies.length) {
                        QuickReplies.appendToLastBotMessage(data.quickReplies);
                    }

                    if (data && data.suggestedProducts && data.suggestedProducts.length) {
                        Feedback.offerOnLastBotMessage();
                    }
                })
                .fail(function (xhr) {
                    if (token !== requestToken) {
                        return;
                    }

                    var fallback = "Something went wrong. Please try again.";
                    try {
                        var errorBody = xhr && xhr.responseJSON;
                        if (errorBody && errorBody.message) {
                            fallback = errorBody.message;
                        }
                    } catch (e) { /* ignore */ }

                    if (history.length && history[history.length - 1].role === "user") {
                        history.pop();
                    }

                    Messaging.setTyping(false);
                    Messaging.add(fallback, "bot");
                })
                .always(function () {
                    if (token !== requestToken) {
                        return;
                    }

                    isSending = false;
                    Panel.setComposerDisabled(false);
                    $("#guided-selling-input").trigger("focus");
                });
        },

        add: function (text, role) {
            Messaging.addEntry({ role: role, text: text });
        },

        /**
         * Escape HTML, then turn markdown-style **bold** into &lt;strong&gt;.
         * Used for bot replies only — user bubbles stay plain text.
         */
        formatBotText: function (text) {
            var escaped = $("<div></div>").text(text || "").html();
            return escaped.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
        },

        /**
         * Single entry point for chat turns: stores the turn in the transcript so it can be
         * rebuilt after a page load, then renders it.
         */
        addEntry: function (entry) {
            transcript.push(entry);
            Messaging.renderEntry(entry);

            if (entry.role === "user") {
                Panel.setResetVisible(true);
            }

            Scroll.sync();
            ChatStorage.save();
            return entry;
        },

        /** Renders a transcript entry — used for both live turns and restored ones. */
        renderEntry: function (entry) {
            var $messages = $("#guided-selling-messages");
            var $message = $("<div></div>")
                .addClass("guided-selling-message guided-selling-message--" + entry.role)
                .data(ENTRY_DATA_KEY, entry);

            if (entry.role === "user") {
                $message.append(
                    $("<div></div>")
                        .addClass("guided-selling-message__bubble")
                        .append($("<p></p>").text(entry.text || ""))
                );
            } else if (entry.text) {
                $message.append($("<p></p>").html(Messaging.formatBotText(entry.text)));
            }

            if (entry.products && entry.products.length) {
                $message.append(Products.buildCards(entry.products));
            }

            if (entry.compareProducts) {
                var $compare = Products.buildCompare(entry.compareProducts);
                if ($compare) {
                    $message.append($compare);
                }
            }

            if (entry.dealer) {
                $message.append(Dealers.buildCard(entry.dealer));
            }

            if (entry.groups && entry.groups.length) {
                $message.append(
                    $("<div></div>")
                        .addClass("guided-selling-action-groups")
                        .append(PredefinedActions.buildGroups(entry.groups, entry.selected))
                );
            }

            if (entry.progress) {
                $message.append(
                    PredefinedActions.buildProgress(entry.progress.step, entry.progress.total)
                );
            }

            if (entry.quickReplies && entry.quickReplies.length) {
                $message.append(
                    QuickReplies.build(entry.quickReplies, entry.selectedQuickReply)
                );
            }

            if (entry.showFeedback) {
                $message.append(Feedback.build());
            }

            $messages.find(".guided-selling-message--typing").remove();
            Messaging.appendElement($message);
        },

        /** The transcript entry a rendered message belongs to, if any (welcome turn has none). */
        getEntry: function ($element) {
            var $message = $element.closest(".guided-selling-message");
            return $message.length ? $message.data(ENTRY_DATA_KEY) : null;
        },

        setTyping: function (isTyping) {
            var $messages = $("#guided-selling-messages");
            $messages.find(".guided-selling-message--typing").remove();

            if (!isTyping) {
                Scroll.sync();
                return;
            }

            var $typing = $("<div></div>")
                .addClass("guided-selling-message guided-selling-message--bot guided-selling-message--typing is-visible")
                .attr("aria-label", "Cadence is thinking")
                .attr("role", "status")
                .html("<span></span><span></span><span></span>");

            Messaging.appendElement($typing);
            Scroll.sync();
        },

        appendElement: function ($element) {
            var $messages = $("#guided-selling-messages");
            var $spacer = $messages.children(".guided-selling-scroll-spacer");

            if ($spacer.length) {
                $element.insertBefore($spacer);
            } else {
                $messages.append($element);
                Scroll.ensureSpacer();
            }
        }
    };

    // -------------------------------------------------------------------------
    // Products — suggested product cards
    // -------------------------------------------------------------------------

    var Products = {
        normalizeImageUrl: function (imageUrl, width) {
            if (!imageUrl) {
                return "";
            }

            var transform = IMAGE_TRANSFORM_TO.replace(IMAGE_TRANSFORM_WIDTH_TOKEN, width);
            return String(imageUrl).split(IMAGE_TRANSFORM_FROM).join(transform);
        },

        /**
         * Maps a suggestedProducts item from the guided-selling API onto a stable shape
         * for cards, compare links, and session restore. Prefer reason (current API);
         * fall back to description for older stored sessions.
         */
        normalizeSuggestion: function (product) {
            if (!product) {
                return null;
            }

            return {
                productId: product.bikesGlobalModelId,
                name: product.name || "",
                reason: product.reason || product.description || "",
                isBestMatch: !!product.isBestMatch,
                isPerfectMatch: !!product.isPerfectMatch,
                unmetRequirements: $.isArray(product.unmetRequirements)
                    ? $.grep(product.unmetRequirements, function (item) {
                        return !!$.trim(String(item || ""));
                    })
                    : [],
                brand: product.brand || "",
                divisionCode: product.divisionCode || "",
                category: product.category || "",
                url: product.url || "",
                imageUrl: product.imageUrl || "",
                priceDisplay: product.priceDisplay || "",
                groupset: product.groupset || "",
                frame: product.frame || "",
                colors: $.isArray(product.colors)
                    ? $.grep(product.colors, function (item) {
                        return !!$.trim(String(item || ""));
                    })
                    : []
            };
        },

        /**
         * Reason copy, plus a separate line for unmet requirements when the suggestion
         * is not a perfect match (API: UnmetRequirements).
         */
        buildDescription: function (suggestion) {
            var $fragments = $();
            var reason = suggestion.reason || "";
            var unmet = suggestion.unmetRequirements || [];

            if (reason) {
                $fragments = $fragments.add(
                    $("<p></p>")
                        .addClass("guided-selling-product-card__description")
                        .text(reason)
                );
            }

            if (!suggestion.isPerfectMatch && unmet.length) {
                $fragments = $fragments.add(
                    $("<p></p>")
                        .addClass("guided-selling-product-card__description guided-selling-product-card__description--mismatch")
                        .append($("<strong></strong>").text("Mismatch: "))
                        .append(document.createTextNode(unmet.join(", ")))
                );
            }

            return $fragments;
        },

        /**
         * Prototype large-card specs: Groupset, Frame, Colors — skip empty rows, cap at three.
         */
        buildSpecs: function (suggestion) {
            var rows = [];
            var groupset = $.trim(suggestion.groupset || "");
            var frame = $.trim(suggestion.frame || "");
            var colorsValue = (suggestion.colors || []).join(", ");

            if (groupset) {
                rows.push({ key: LABEL_SPEC_GROUPSET, value: groupset });
            }

            if (frame) {
                rows.push({ key: LABEL_SPEC_FRAME, value: frame });
            }

            if (colorsValue) {
                rows.push({ key: LABEL_SPEC_COLORS, value: colorsValue });
            }

            rows = rows.slice(0, MAX_SPEC_ROWS);
            if (!rows.length) {
                return null;
            }

            var $dl = $("<dl></dl>").addClass("guided-selling-product-card__specs");

            $.each(rows, function (_, spec) {
                $dl.append(
                    $("<div></div>")
                        .addClass("guided-selling-product-card__spec")
                        .append($("<dt></dt>").text(spec.key))
                        .append($("<dd></dd>").text(spec.value))
                );
            });

            return $dl;
        },

        buildImage: function (suggestion, size) {
            var imageUrl = Products.normalizeImageUrl(suggestion.imageUrl, CARD_IMAGE_WIDTH[size]);
            var $media = $("<div></div>").addClass("guided-selling-product-card__media");

            if (imageUrl) {
                $media.append(
                    $("<img>")
                        .attr("src", imageUrl)
                        .attr("alt", suggestion.name)
                        .addClass("guided-selling-product-card__image")
                );
            }

            return $media;
        },

        /** Prototype ReviewStars — whole stars only, none for the rest of the five. */
        buildRating: function (rating) {
            // temporary disabled for now, we have now product ratings that we can show
            return null;

            var filled = Math.min(MAX_RATING, Math.max(0, Math.round(rating || 0)));
            if (!filled) {
                return null;
            }

            return $("<div></div>")
                .addClass("guided-selling-product-card__rating")
                .attr("aria-label", filled + " out of " + MAX_RATING + " stars")
                .html(new Array(filled + 1).join(STAR_ICON_SVG));
        },

        /** Eyebrow row: how well the suggestion fits, before the title on either size. */
        buildLabels: function (suggestion) {
            var $labels = $("<div></div>").addClass("guided-selling-product-card__labels");

            if (suggestion.isBestMatch) {
                $labels.append(
                    $("<p></p>")
                        .addClass("guided-selling-product-card__label guided-selling-product-card__label--best-match")
                        .text(LABEL_BEST_MATCH)
                );
            }

            if (!suggestion.isPerfectMatch) {
                $labels.append(
                    $("<p></p>")
                        .addClass("guided-selling-product-card__label guided-selling-product-card__label--close-match")
                        .text(LABEL_CLOSE_MATCH)
                );
            }

            return $labels.children().length ? $labels : null;
        },

        buildAward: function (award) {
            if (!award) {
                return null;
            }

            return $("<div></div>")
                .addClass("guided-selling-product-card__award")
                .append(
                    $("<span></span>")
                        .addClass("guided-selling-product-card__award-icon")
                        .html(TROPHY_ICON_SVG)
                )
                .append($("<p></p>").text(award));
        },

        /**
         * Prototype ProductCard Size=Large — image band across the card, then title and
         * stars against the price, with the award behind a divider.
         */
        buildLargeBody: function (suggestion) {
            var $heading = $("<div></div>")
                .addClass("guided-selling-product-card__heading")
                .append(
                    $("<div></div>")
                        .addClass("guided-selling-product-card__heading-text")
                        .append(
                            $("<h3></h3>")
                                .addClass("guided-selling-product-card__title")
                                .text(suggestion.name)
                        )
                        .append(Products.buildRating(PLACEHOLDER_RATING))
                );

            if (suggestion.priceDisplay) {
                $heading.append(
                    $("<p></p>")
                        .addClass("guided-selling-product-card__price")
                        .text(suggestion.priceDisplay)
                );
            }

            var $copy = $("<div></div>")
                .addClass("guided-selling-product-card__copy")
                .append(
                    $("<div></div>")
                        .addClass("guided-selling-product-card__copy-main")
                        .append(Products.buildLabels(suggestion))
                        .append($heading)
                        .append(Products.buildDescription(suggestion))
                        .append(Products.buildSpecs(suggestion))
                );

            // Temporarily disabled until product awards are available from the API.
            // var $award = Products.buildAward(PLACEHOLDER_AWARD);
            // if ($award) {
            //     $copy
            //         .append($("<div></div>").addClass("guided-selling-product-card__divider"))
            //         .append($award);
            // }

            return $("<div></div>")
                .addClass("guided-selling-product-card__body")
                .append(Products.buildImage(suggestion, CARD_SIZE_LARGE))
                .append($copy);
        },

        /**
         * Prototype ProductCard Size=Small — narrow image column, then the copy with the
         * price top right. No stars and no award on this size, by design.
         */
        buildSmallBody: function (suggestion) {
            var $main = $("<div></div>")
                .addClass("guided-selling-product-card__copy-main")
                .append(Products.buildLabels(suggestion));

            var $heading = $("<div></div>")
                .addClass("guided-selling-product-card__heading")
                .append(
                    $("<h3></h3>")
                        .addClass("guided-selling-product-card__title")
                        .text(suggestion.name)
                );

            if (suggestion.priceDisplay) {
                $heading.append(
                    $("<p></p>")
                        .addClass("guided-selling-product-card__price")
                        .text(suggestion.priceDisplay)
                );
            }

            $main.append($heading);

            var $copy = $("<div></div>")
                .addClass("guided-selling-product-card__copy")
                .append($main)
                .append(Products.buildDescription(suggestion));

            return $("<div></div>")
                .addClass("guided-selling-product-card__body")
                .append(Products.buildImage(suggestion, CARD_SIZE_SMALL))
                .append($copy);
        },

        /**
         * Prototype ProductCard — the border, radius and hover are shared, only the body
         * changes per size. isPerfectMatch is exposed as a data attribute for upcoming UI.
         */
        buildCard: function (product, size) {
            var suggestion = Products.normalizeSuggestion(product) || {};
            var cardSize = size === CARD_SIZE_SMALL ? CARD_SIZE_SMALL : CARD_SIZE_LARGE;
            var $article = $("<article></article>")
                .addClass("guided-selling-product-card__inner")
                .append(
                    cardSize === CARD_SIZE_SMALL
                        ? Products.buildSmallBody(suggestion)
                        : Products.buildLargeBody(suggestion)
                );

            var $card = suggestion.url
                ? $("<a></a>")
                    .addClass("guided-selling-product-card")
                    .attr("href", suggestion.url)
                    .attr("aria-label", suggestion.name + ", view product")
                    .append($article)
                : $("<div></div>")
                    .addClass("guided-selling-product-card")
                    .append($article);

            $card
                .addClass("guided-selling-product-card--" + cardSize)
                .attr("data-is-best-match", suggestion.isBestMatch ? "true" : "false")
                .attr("data-is-perfect-match", suggestion.isPerfectMatch ? "true" : "false");

            if (suggestion.productId != null && suggestion.productId !== "") {
                $card.attr("data-product-id", suggestion.productId);
            }

            return $card;
        },

        /**
         * A lone suggestion gets the large card; a shortlist gets the small ones, stacked
         * flush into a single bordered list instead of separate cards.
         */
        buildCards: function (products) {
            var list = $.isArray(products) ? products : [products];
            var isShortlist = list.length > 1;
            var size = isShortlist ? CARD_SIZE_SMALL : CARD_SIZE_LARGE;
            var $cards = $("<div></div>")
                .addClass("guided-selling-product-cards")
                .toggleClass("guided-selling-product-cards--stacked", isShortlist);

            $.each(list, function (_, product) {
                $cards.append(Products.buildCard(product, size));
            });

            return $cards;
        },

        /** Global-catalogue ids for the comparison route, capped at what that page can show. */
        collectCompareIds: function (products) {
            var ids = [];

            $.each(products || [], function (_, product) {
                if (ids.length >= MAX_COMPARE_PRODUCTS) {
                    return false;
                }

                var id = $.trim(String((product && product.productId) || ""));
                if (/^\d+$/.test(id) && $.inArray(id, ids) < 0) {
                    ids.push(id);
                }
            });

            return ids;
        },

        /** Compare link for multiple suggestions, or null when fewer than two ids are usable. */
        buildCompare: function (products) {
            var ids = Products.collectCompareIds(products);
            if (ids.length < 2) {
                return null;
            }

            var comparison = $.map(ids, function (id) {
                return COMPARE_ID_PREFIX + id;
            }).join("-");

            return $("<div></div>")
                .addClass("discovermode-button-component-wrapper")
                .append(
                    $("<a></a>")
                        .addClass("button-component filled fullwidth")
                        .attr("href", Config.getCulturePrefix() + "/comparison/" + comparison)
                        .text("Compare " + ids.length + " bikes")
                );
        },

        /** Cards for every suggestion; several of them also get a link to the comparison page. */
        appendSuggestionsToLastBotMessage: function (products) {
            var $lastBot = $("#guided-selling-messages .guided-selling-message--bot").last();
            if (!$lastBot.length || !products || !products.length) {
                return;
            }

            var suggestions = $.map(products, function (product) {
                return Products.normalizeSuggestion(product);
            });

            if (!suggestions.length) {
                return;
            }

            $lastBot.append(Products.buildCards(suggestions));

            var $compare = suggestions.length > 1 ? Products.buildCompare(suggestions) : null;
            if ($compare) {
                $lastBot.append($compare);
            }

            var entry = Messaging.getEntry($lastBot);
            if (entry) {
                entry.products = suggestions;
                if ($compare) {
                    entry.compareProducts = suggestions;
                }
                ChatStorage.save();
            }

            Scroll.sync();
        }
    };

    // -------------------------------------------------------------------------
    // Dealers — prototype DealerStockCard for the nearest suggested dealer
    // -------------------------------------------------------------------------

    var Dealers = {
        /**
         * Maps a suggestedDealers item onto the prototype DealerStockCard shape. Opening
         * hours are not on the response yet, so those stay placeholders.
         */
        normalizeSuggestion: function (dealer, products) {
            if (!dealer) {
                return null;
            }

            var inStockItems = Dealers.normalizeInStockItems(dealer.inStockItems);
            var sizes = Dealers.collectSizes(inStockItems, dealer.inStockSizes);

            var productId = dealer.inStockProductId;
            if (productId == null) {
                var list = $.isArray(products) ? products : [];
                if (list.length === 1 && list[0]) {
                    productId = list[0].bikesGlobalModelId != null
                        ? list[0].bikesGlobalModelId
                        : list[0].productId;
                }
            }

            return {
                dealerId: dealer.dealerId,
                dealerCode: $.trim(dealer.dealerCode || ""),
                name: dealer.name || "",
                address: Dealers.buildAddress(dealer),
                distanceDisplay: dealer.distanceDisplay || "",
                url: Dealers.normalizeUrl(dealer.webAddress),
                isGiantStore: !!dealer.isGiantStore,
                inStockItems: inStockItems,
                sizes: sizes,
                selectedSize: sizes.length === 1 ? sizes[0] : (dealer.selectedSize || ""),
                productId: productId,
                productName: Dealers.resolveProductName(dealer, products),
                imageUrl: $.trim(dealer.summaryImage || "") || PLACEHOLDER_DEALER_IMAGE,
                openingHours: PLACEHOLDER_DEALER_HOURS
            };
        },

        normalizeInStockItems: function (items) {
            if (!$.isArray(items)) {
                return [];
            }

            return $.grep($.map(items, function (item) {
                if (!item) {
                    return null;
                }

                var partnumberCode = $.trim(item.partnumberCode || "");
                if (!partnumberCode) {
                    return null;
                }

                return {
                    size: $.trim(item.size || ""),
                    partnumberCode: partnumberCode
                };
            }), function (item) {
                return !!item;
            });
        },

        collectSizes: function (inStockItems, fallbackSizes) {
            var sizes = [];

            $.each(inStockItems || [], function (_, item) {
                if (item.size && $.inArray(item.size, sizes) === -1) {
                    sizes.push(item.size);
                }
            });

            if (sizes.length) {
                return sizes;
            }

            return $.isArray(fallbackSizes)
                ? $.grep(fallbackSizes, function (size) {
                    return !!$.trim(String(size || ""));
                })
                : [];
        },

        partnumberForSize: function (dealer, size) {
            var items = (dealer && dealer.inStockItems) || [];
            var match = "";
            var requested = $.trim(size || "").toLowerCase();

            $.each(items, function (_, item) {
                if (requested && item.size && item.size.toLowerCase() === requested) {
                    match = item.partnumberCode;
                    return false;
                }
            });

            if (!match && items.length === 1) {
                match = items[0].partnumberCode;
            }

            return match;
        },

        buildAddress: function (dealer) {
            var postal = $.trim([dealer.zipcode || "", dealer.city || ""].join(" "));
            var parts = $.grep([$.trim(dealer.street || ""), postal], function (part) {
                return !!part;
            });

            return parts.join(", ");
        },

        /** Dealer web addresses are stored without a scheme as often as with one. */
        normalizeUrl: function (webAddress) {
            var url = $.trim(webAddress || "");
            if (!url) {
                return "";
            }

            return /^https?:\/\//i.test(url) ? url : "https://" + url;
        },

        /** The bike the stock was confirmed for, named by the suggestion it came in with. */
        resolveProductName: function (dealer, products) {
            var list = $.isArray(products) ? products : [];
            var name = "";

            $.each(list, function (_, product) {
                var id = product && (product.bikesGlobalModelId != null
                    ? product.bikesGlobalModelId
                    : product.productId);
                if (
                    product
                    && dealer.inStockProductId != null
                    && String(id) === String(dealer.inStockProductId)
                ) {
                    name = $.trim(product.name || "");
                    return false;
                }
            });

            if (!name && list.length === 1 && list[0]) {
                name = $.trim(list[0].name || "");
            }

            return name || LABEL_DEALER_PRODUCT_FALLBACK;
        },

        /** Prototype getOpeningHoursSummary, read off today's row rather than the first open one. */
        getHoursSummary: function (openingHours) {
            // The placeholder schedule starts on Monday; Date.getDay() starts on Sunday.
            var today = openingHours[(new Date().getDay() + 6) % 7];
            if (!today || /^closed/i.test(today.hours)) {
                return LABEL_DEALER_CLOSED_TODAY;
            }

            var closingTime = /(?:-|–|to)\s*(\d{1,2}:\d{2})\s*$/i.exec(today.hours);
            return closingTime ? LABEL_DEALER_OPEN_NOW + closingTime[1] : today.hours;
        },

        buildIcon: function (svg) {
            return $("<span></span>")
                .addClass("guided-selling-dealer-card__icon")
                .html(svg);
        },

        /** Photo and store title both lead to the store's own site when there is one. */
        buildLink: function (dealer, className, $content) {
            var $element = dealer.url
                ? $("<a></a>")
                    .attr("href", dealer.url)
                    .attr("target", "_blank")
                    .attr("rel", "noopener noreferrer")
                : $("<span></span>");

            return $element.addClass(className).append($content);
        },

        buildMedia: function (dealer) {
            var $image = $("<img>")
                .attr("src", dealer.imageUrl)
                .attr("alt", dealer.name)
                .addClass("guided-selling-dealer-card__image");

            return Dealers.buildLink(dealer, "guided-selling-dealer-card__media", $image);
        },

        /** Figma: Matching tag — the stock pill, or which kind of store it is without stock. */
        buildStatus: function (dealer) {
            var inStock = dealer.sizes.length > 0;
            if (!inStock && !dealer.isGiantStore) {
                return null;
            }

            return $("<span></span>")
                .addClass("guided-selling-dealer-card__status")
                .toggleClass("guided-selling-dealer-card__status--store", !inStock)
                .text(inStock ? LABEL_DEALER_IN_STOCK : LABEL_DEALER_GIANT_STORE);
        },

        buildHoursList: function (dealer) {
            var $list = $("<dl></dl>")
                .addClass("guided-selling-dealer-card__hours-list")
                .attr("hidden", "hidden");

            $.each(dealer.openingHours, function (_, row) {
                $list.append(
                    $("<div></div>")
                        .addClass("guided-selling-dealer-card__hours-row")
                        .append($("<dt></dt>").text(row.day))
                        .append($("<dd></dd>").text(row.hours))
                );
            });

            return $list;
        },

        buildHours: function (dealer) {
            var summary = Dealers.getHoursSummary(dealer.openingHours);
            var $toggle = $("<button></button>")
                .attr("type", "button")
                .attr("aria-expanded", "false")
                .attr("aria-label", "Opening hours")
                .addClass("guided-selling-dealer-card__hours-toggle")
                .append(Dealers.buildIcon(CLOCK_ICON_SVG));

            if (summary !== LABEL_DEALER_CLOSED_TODAY) {
                $toggle.append(
                    $("<span></span>")
                        .addClass("guided-selling-dealer-card__open-dot")
                        .attr("aria-hidden", "true")
                );
            }

            $toggle
                .append(
                    $("<span></span>")
                        .addClass("guided-selling-dealer-card__hours-summary")
                        .text(summary)
                )
                .append(
                    Dealers.buildIcon(CHEVRON_ICON_SVG)
                        .addClass("guided-selling-dealer-card__chevron")
                );

            return $("<div></div>")
                .addClass("guided-selling-dealer-card__hours")
                .append($toggle)
                .append(Dealers.buildHoursList(dealer));
        },

        buildAddressRow: function (dealer) {
            var text = $.grep([dealer.address, dealer.distanceDisplay], function (part) {
                return !!part;
            }).join(" · ");

            if (!text) {
                return null;
            }

            return $("<div></div>")
                .addClass("guided-selling-dealer-card__row")
                .append(Dealers.buildIcon(MAP_PIN_ICON_SVG))
                .append($("<span></span>").text(text));
        },

        canOrderFromStore: function (dealer) {
            return !!(dealer
                && dealer.productId != null
                && dealer.productId !== ""
                && dealer.dealerCode
                && dealer.inStockItems
                && dealer.inStockItems.length);
        },

        /** Figma: Option Chips — the sizes on the floor, only when stock was confirmed. */
        buildStock: function (dealer) {
            if (!dealer.sizes.length) {
                return null;
            }

            var $stock = $("<div></div>")
                .addClass("guided-selling-dealer-card__stock")
                .append(
                    $("<p></p>")
                        .addClass("guided-selling-dealer-card__stock-label")
                        .text(dealer.productName + " in store:")
                );

            $.each(dealer.sizes, function (_, size) {
                var selected = size === dealer.selectedSize;
                $stock.append(
                    $("<button></button>")
                        .attr("type", "button")
                        .attr("data-size", size)
                        .attr("aria-pressed", selected ? "true" : "false")
                        .addClass("guided-selling-dealer-card__size")
                        .toggleClass("guided-selling-dealer-card__size--selected", selected)
                        .text(size)
                );
            });

            return $stock;
        },

        /**
         * Book a test ride still goes to the store site. Order from store adds the
         * in-stock bike to the cart, same as the PDP "Order from this store" button.
         */
        buildActions: function (dealer) {
            var canOrder = Dealers.canOrderFromStore(dealer);
            if (!dealer.url && !canOrder) {
                return null;
            }

            var $actions = $("<div></div>").addClass("guided-selling-dealer-card__actions");

            if (dealer.url) {
                $actions.append(
                    $("<a></a>")
                        .attr("href", dealer.url)
                        .attr("target", "_blank")
                        .attr("rel", "noopener noreferrer")
                        .addClass("guided-selling-dealer-card__action")
                        .addClass("guided-selling-dealer-card__action--primary")
                        .text(LABEL_BOOK_TEST_RIDE)
                );
            }

            if (canOrder) {
                $actions.append(
                    $("<button></button>")
                        .attr("type", "button")
                        .addClass("guided-selling-dealer-card__action")
                        .addClass("guided-selling-dealer-card__action--secondary")
                        .addClass("guided-selling-dealer-card__action--order")
                        .text(LABEL_ORDER_FROM_STORE)
                );
            } else if (dealer.url) {
                $actions.append(
                    $("<a></a>")
                        .attr("href", dealer.url)
                        .attr("target", "_blank")
                        .attr("rel", "noopener noreferrer")
                        .addClass("guided-selling-dealer-card__action")
                        .addClass("guided-selling-dealer-card__action--secondary")
                        .text(LABEL_ORDER_FROM_STORE)
                );
            }

            return $actions;
        },

        buildOrderStatus: function () {
            return $("<p></p>")
                .addClass("guided-selling-dealer-card__order-status")
                .attr("hidden", "hidden");
        },

        setOrderStatus: function ($card, message) {
            var $status = $card.find(".guided-selling-dealer-card__order-status");
            if (message) {
                $status.text(message).removeAttr("hidden");
            } else {
                $status.text("").attr("hidden", "hidden");
            }
        },

        selectSize: function ($button) {
            var $card = $button.closest(".guided-selling-dealer-card");
            var size = $.trim($button.attr("data-size") || "");
            var entry = Messaging.getEntry($card);
            var dealer = entry && entry.dealer;

            if (!dealer || !size) {
                return;
            }

            dealer.selectedSize = size;
            $card.find(".guided-selling-dealer-card__size")
                .removeClass("guided-selling-dealer-card__size--selected")
                .removeClass("guided-selling-dealer-card__size--needs-selection")
                .attr("aria-pressed", "false");
            $button
                .addClass("guided-selling-dealer-card__size--selected")
                .attr("aria-pressed", "true");
            Dealers.setOrderStatus($card, "");
            ChatStorage.save();
        },

        orderFromStore: function ($button) {
            var $card = $button.closest(".guided-selling-dealer-card");
            var entry = Messaging.getEntry($card);
            var dealer = entry && entry.dealer;

            if (!Dealers.canOrderFromStore(dealer) || $button.prop("disabled")) {
                return;
            }

            var size = $.trim(dealer.selectedSize || "");
            if (!size && dealer.sizes.length === 1) {
                size = dealer.sizes[0];
                dealer.selectedSize = size;
            }

            if (!size) {
                $card.find(".guided-selling-dealer-card__size")
                    .addClass("guided-selling-dealer-card__size--needs-selection");
                Dealers.setOrderStatus($card, LABEL_SELECT_SIZE);
                return;
            }

            var partnumberCode = Dealers.partnumberForSize(dealer, size);
            if (!partnumberCode) {
                Dealers.setOrderStatus($card, LABEL_SELECT_SIZE);
                return;
            }

            $button.prop("disabled", true).attr("aria-busy", "true").text(LABEL_ORDER_ADDING);
            Dealers.setOrderStatus($card, "");

            $.ajax({
                url: Config.getAddCartItemUrl(),
                method: "POST",
                data: {
                    productId: dealer.productId,
                    partnumberCode: partnumberCode,
                    productType: 1,
                    quantity: 1,
                    dealerCode: dealer.dealerCode,
                    globalCatalogue: true,
                    GAclientId: Config.getGaClientId()
                }
            })
                .done(function (data) {
                    $button.prop("disabled", false).removeAttr("aria-busy").text(LABEL_ORDER_FROM_STORE);

                    if (data && data.result > 0) {
                        Dealers.refreshCart(dealer.productId, partnumberCode);
                        return;
                    }

                    Dealers.setOrderStatus(
                        $card,
                        (data && data.exceptionmessage) || "Something went wrong. Please try again."
                    );
                })
                .fail(function () {
                    $button.prop("disabled", false).removeAttr("aria-busy").text(LABEL_ORDER_FROM_STORE);
                    Dealers.setOrderStatus($card, "Something went wrong. Please try again.");
                });
        },

        refreshCart: function (productId, partnumberCode) {
            if (Panel.isFullscreen()) {
                Panel.close();
            }

            var openMiniCart = function () {
                var cartButton = document.getElementById("cart-summary-container2");
                if (cartButton) {
                    cartButton.click();
                }
            };

            $.get(Config.getCartSummaryUrl(productId, partnumberCode))
                .done(function (cart) {
                    if (window.vueStore && cart) {
                        window.vueStore.commit("setCart", cart);
                    }

                    window.setTimeout(openMiniCart, 0);
                })
                .fail(openMiniCart);
        },

        /** Prototype DealerStockCard — photo, then who they are, what is in, and the actions. */
        buildCard: function (dealer) {
            var $heading = $("<div></div>")
                .addClass("guided-selling-dealer-card__heading")
                .append(
                    $("<h3></h3>")
                        .addClass("guided-selling-dealer-card__title")
                        .append(
                            Dealers.buildLink(
                                dealer,
                                "guided-selling-dealer-card__title-link",
                                document.createTextNode(dealer.name)
                            )
                        )
                )
                .append(Dealers.buildStatus(dealer));

            var $meta = $("<div></div>")
                .addClass("guided-selling-dealer-card__meta")
                // Temporarily disabled until dealer opening hours are available from the API.
                // .append(Dealers.buildHours(dealer))
                .append(Dealers.buildAddressRow(dealer));

            var $body = $("<div></div>")
                .addClass("guided-selling-dealer-card__body")
                .append(
                    $("<div></div>")
                        .addClass("guided-selling-dealer-card__details")
                        .append($heading)
                        .append($meta)
                        .append(Dealers.buildStock(dealer))
                )
                .append(Dealers.buildActions(dealer))
                .append(Dealers.buildOrderStatus());

            var $card = $("<article></article>")
                .addClass("guided-selling-dealer-card")
                .append(Dealers.buildMedia(dealer))
                .append($body);

            if (dealer.dealerId != null && dealer.dealerId !== "") {
                $card.attr("data-dealer-id", dealer.dealerId);
            }

            return $card;
        },

        toggleHours: function ($toggle) {
            var expanded = $toggle.attr("aria-expanded") === "true";
            var $list = $toggle.siblings(".guided-selling-dealer-card__hours-list");

            $toggle.attr("aria-expanded", expanded ? "false" : "true");
            if (expanded) {
                $list.attr("hidden", "hidden");
            } else {
                $list.removeAttr("hidden");
            }

            Scroll.sync();
        },

        /** Only the nearest dealer gets a card — the rest stay in the reply text. */
        appendNearestToLastBotMessage: function (dealers, products) {
            var $lastBot = $("#guided-selling-messages .guided-selling-message--bot").last();
            if (!$lastBot.length || !dealers || !dealers.length) {
                return;
            }

            var dealer = Dealers.normalizeSuggestion(dealers[0], products);
            if (!dealer || !dealer.name) {
                return;
            }

            $lastBot.append(Dealers.buildCard(dealer));

            var entry = Messaging.getEntry($lastBot);
            if (entry) {
                entry.dealer = dealer;
                ChatStorage.save();
            }

            Scroll.sync();
        }
    };

    // -------------------------------------------------------------------------
    // Feedback — "Was that helpful?" after product suggestions (once per conversation)
    // -------------------------------------------------------------------------

    var Feedback = {
        FEEDBACK_SCROLL_GAP: 32,
        SEND_ICON_SVG:
            '<svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">' +
            '<path d="M6 11V2M2.5 5.5L6 2l3.5 3.5" stroke="currentColor" stroke-width="1.6" fill="none" />' +
            "</svg>",

        buildChip: function (label, vote, active, clickable) {
            var $chip = $("<button></button>")
                .attr("type", "button")
                .attr("aria-pressed", active ? "true" : "false")
                .addClass("guided-selling-feedback__chip")
                .text(label);

            if (clickable) {
                $chip.attr("data-vote", vote);
            } else {
                $chip.prop("disabled", true);
            }

            return $chip;
        },

        buildPrompt: function (text) {
            return $("<p></p>")
                .addClass("guided-selling-feedback__prompt")
                .text(text);
        },

        buildNoteForm: function () {
            return $("<form></form>")
                .addClass("guided-selling-feedback__form")
                .append(
                    $("<input>")
                        .attr("type", "text")
                        .attr("name", "feedback")
                        .attr("placeholder", "Your feedback")
                        .attr("aria-label", "Your feedback")
                        .attr("maxlength", "2000")
                        .addClass("guided-selling-feedback__input")
                )
                .append(
                    $("<button></button>")
                        .attr("type", "submit")
                        .attr("aria-label", "Send feedback")
                        .addClass("guided-selling-feedback__send")
                        .html(Feedback.SEND_ICON_SVG)
                );
        },

        build: function () {
            var $root = $("<div></div>").addClass("guided-selling-feedback");
            var $row = $("<div></div>").addClass("guided-selling-feedback__row");

            if (feedbackVote === "yes") {
                return $root.append(
                    $row
                        .append(Feedback.buildChip("Yes", "yes", true, false))
                        .append(Feedback.buildPrompt("Thanks for the feedback!"))
                );
            }

            if (feedbackVote === "no") {
                $row
                    .append(Feedback.buildChip("No", "no", true, false))
                    .append(
                        Feedback.buildPrompt(
                            feedbackNoteSent
                                ? "Thanks for the feedback!"
                                : "Thanks for the feedback! Could you let us know why this was not helpful?"
                        )
                    );
                $root.append($row);
                if (!feedbackNoteSent) {
                    $root.append(Feedback.buildNoteForm());
                }
                return $root;
            }

            return $root.append(
                $row
                    .append(Feedback.buildPrompt("Was that helpful?"))
                    .append(Feedback.buildChip("Yes", "yes", false, true))
                    .append(Feedback.buildChip("No", "no", false, true))
            );
        },

        clearShowFeedback: function () {
            $.each(transcript, function (_, entry) {
                if (entry) {
                    entry.showFeedback = false;
                }
            });
            $("#guided-selling-messages .guided-selling-feedback").remove();
        },

        /** Show the widget on the latest bot turn with products, unless already answered. */
        offerOnLastBotMessage: function () {
            if (feedbackVote) {
                return;
            }

            var $lastBot = $("#guided-selling-messages .guided-selling-message--bot").last();
            if (!$lastBot.length || $lastBot.hasClass("guided-selling-message--typing")) {
                return;
            }

            var entry = Messaging.getEntry($lastBot);
            if (!entry || !entry.products || !entry.products.length) {
                return;
            }

            Feedback.clearShowFeedback();
            entry.showFeedback = true;
            $lastBot.append(Feedback.build());
            ChatStorage.save();
            Scroll.sync();
        },

        /**
         * After a restored transcript: keep a locked widget where it was, or attach an
         * unanswered one to the last product suggestion.
         */
        hydrate: function () {
            var hasWidget = false;
            $.each(transcript, function (_, entry) {
                if (entry && entry.showFeedback) {
                    hasWidget = true;
                    return false;
                }
            });

            if (hasWidget) {
                if (feedbackVote === "no" && !feedbackNoteSent) {
                    Feedback.focusNoteInput();
                }
                return;
            }

            var lastProductEntry = null;
            $.each(transcript, function (_, entry) {
                if (entry && entry.role === "bot" && entry.products && entry.products.length) {
                    lastProductEntry = entry;
                }
            });

            if (!lastProductEntry || feedbackVote) {
                return;
            }

            lastProductEntry.showFeedback = true;
            var $messages = $("#guided-selling-messages .guided-selling-message--bot");
            $messages.each(function () {
                var $message = $(this);
                if (Messaging.getEntry($message) === lastProductEntry) {
                    $message.append(Feedback.build());
                    return false;
                }
            });
            ChatStorage.save();
        },

        replaceWidget: function ($feedback) {
            var $next = Feedback.build();
            $feedback.replaceWith($next);
            ChatStorage.save();
            Scroll.sync();
            if (feedbackVote === "no" && !feedbackNoteSent) {
                Feedback.focusNoteInput();
            }
        },

        focusNoteInput: function () {
            var input = document.querySelector(".guided-selling-feedback__input");
            if (!input) {
                return;
            }

            input.focus();

            var nudge = function () {
                var scroller = document.getElementById("guided-selling-messages");
                if (!scroller) {
                    return;
                }

                var overshoot = input.getBoundingClientRect().bottom
                    + Feedback.FEEDBACK_SCROLL_GAP
                    - scroller.getBoundingClientRect().bottom;
                if (overshoot > 0) {
                    if (typeof scroller.scrollTo === "function") {
                        scroller.scrollTo({
                            top: scroller.scrollTop + overshoot,
                            behavior: "smooth"
                        });
                    } else {
                        scroller.scrollTop += overshoot;
                    }
                }
            };

            if (typeof window.requestAnimationFrame === "function") {
                window.requestAnimationFrame(nudge);
            } else {
                window.setTimeout(nudge, 0);
            }
        },

        handleVote: function ($chip) {
            if (feedbackVote || !$chip || !$chip.length) {
                return;
            }

            var vote = $chip.attr("data-vote");
            if (vote !== "yes" && vote !== "no") {
                return;
            }

            feedbackVote = vote;
            Feedback.submitVote(vote === "yes");
            Feedback.replaceWidget($chip.closest(".guided-selling-feedback"));
        },

        handleNote: function ($form) {
            if (feedbackVote !== "no" || feedbackNoteSent || !$form || !$form.length) {
                return;
            }

            var reason = $.trim($form.find(".guided-selling-feedback__input").val() || "");
            feedbackNoteSent = true;
            if (reason) {
                Feedback.submitVote(false, reason);
            }
            Feedback.replaceWidget($form.closest(".guided-selling-feedback"));
        },

        submitVote: function (wasHelpful, notHelpfulReason) {
            if (!conversationId) {
                return;
            }

            var payload = { wasHelpful: !!wasHelpful };
            if (!wasHelpful && notHelpfulReason) {
                payload.notHelpfulReason = notHelpfulReason;
            }

            $.ajax({
                url: Config.getFeedbackUrl(conversationId),
                method: "PATCH",
                contentType: "application/json; charset=utf-8",
                data: JSON.stringify(payload)
            });
        }
    };

    // -------------------------------------------------------------------------
    // Scroll — spacer, anchor sync, fade edges
    // -------------------------------------------------------------------------

    var Scroll = {
        /**
         * Keep the spacer as the last child so new turns insert above it
         * (prototype filler that lets the latest human message sit at the top).
         */
        ensureSpacer: function () {
            var $messages = $("#guided-selling-messages");
            var $spacer = $messages.children(".guided-selling-scroll-spacer");

            if (!$spacer.length) {
                $spacer = $("<div></div>")
                    .addClass("guided-selling-scroll-spacer")
                    .attr("aria-hidden", "true");
                $messages.append($spacer);
            } else if (!$spacer.is(":last-child")) {
                $messages.append($spacer);
            }

            return $spacer[0];
        },

        /**
         * Prototype ChatPanel scroll: size the bottom spacer so the latest user turn can
         * reach the top of the panel, then scroll to that anchor once per user turn.
         */
        sync: function (options) {
            options = options || {};
            var scroller = document.getElementById("guided-selling-messages");
            var spacer = Scroll.ensureSpacer();
            if (!scroller || !spacer) {
                return;
            }

            Scroll.updateFades();

            var $userMessages = $(scroller).children(".guided-selling-message--user");
            var anchor = $userMessages.length ? $userMessages.last()[0] : null;

            var bottom = spacer.offsetTop;
            var target = anchor
                ? Math.max(0, anchor.offsetTop - ANCHOR_TOP_OFFSET)
                : Math.max(0, bottom - scroller.clientHeight);

            spacer.style.height = Math.max(0, target + scroller.clientHeight - bottom) + "px";

            if (anchor && anchor === scrolledToAnchor && !options.instant) {
                Scroll.updateFades();
                return;
            }

            scrolledToAnchor = anchor;

            if (typeof scroller.scrollTo === "function") {
                scroller.scrollTo({
                    top: target,
                    behavior: (anchor && !options.instant) ? "smooth" : "auto"
                });
            } else {
                scroller.scrollTop = target;
            }

            Scroll.updateFades();
        },

        updateFades: function () {
            var scroller = document.getElementById("guided-selling-messages");
            if (!scroller) {
                return;
            }

            var topFade = document.querySelector(".chat-fade-top");
            var bottomFade = document.querySelector(".chat-fade-bottom");
            if (!topFade || !bottomFade) {
                return;
            }

            topFade.classList.toggle("is-visible", scroller.scrollTop > 1);
            bottomFade.classList.toggle(
                "is-visible",
                scroller.scrollTop + scroller.clientHeight < scroller.scrollHeight - 1
            );
        }
    };

    // -------------------------------------------------------------------------
    // Init
    // -------------------------------------------------------------------------

    function bindEvents() {
        $("#guided-selling-messages").on("scroll", Scroll.updateFades);

        $(document).on("click", ".toggle-guided-selling", function () {
            Panel.toggle();
        });

        $(document).on("keydown", function (e) {
            if (e.key === "Escape" && $("body").hasClass("guided-selling-open")) {
                Panel.close();
            }
        });

        $(document).on("submit", "#guided-selling-composer", function (e) {
            e.preventDefault();
            Messaging.send();
        });

        $(document).on("click", "#guided-selling-reset", function () {
            Panel.reset();
        });

        $(document).on("click", ".guided-selling-action", function () {
            PredefinedActions.handleClick($(this));
        });

        $(document).on("click", ".guided-selling-quick-reply", function () {
            QuickReplies.handleClick($(this));
        });

        $(document).on("click", ".guided-selling-dealer-card__hours-toggle", function () {
            Dealers.toggleHours($(this));
        });

        $(document).on("click", ".guided-selling-dealer-card__size", function () {
            Dealers.selectSize($(this));
        });

        $(document).on("click", ".guided-selling-dealer-card__action--order", function () {
            Dealers.orderFromStore($(this));
        });

        $(document).on("click", ".guided-selling-feedback__chip[data-vote]", function () {
            Feedback.handleVote($(this));
        });

        $(document).on("submit", ".guided-selling-feedback__form", function (e) {
            e.preventDefault();
            Feedback.handleNote($(this));
        });
    }

    function onReady() {
        var $panel = $("#guided-selling-panel");
        if ($panel.length) {
            $panel.appendTo("body");
        } else {
            // guided selling is not enabled
            return;
        }

        bindEvents();
        ChatStorage.restore();
    }

    // -------------------------------------------------------------------------
    // Public API
    // -------------------------------------------------------------------------

    return {
        onReady: onReady,
        closePanel: Panel.close
    };
})(jQuery);

$(document).ready(function () {
    GuidedSelling.onReady();
});
